/**
 * Formatting helpers for the dashboard (task T.C2).
 *
 * Deliberately dependency-free and deterministic — the `unit` Playwright project
 * runs them in plain Node, and the e2e asserts on their output. Everything is
 * locale-independent on purpose: `toLocaleString` would make both the tests and
 * two managers' screens disagree about the same number.
 */

/** Absence marker for every nullable cell. */
export const MISSING = '—';

/** Thousands separator: NBSP, written as an escape so no editor "fixes" it. */
const NBSP = '\u00a0';

/**
 * `37.5` → `"38%"`. Rounds half-up to whole percents (a table column has no
 * room for `4.5454…`), clamps negatives to `0`, and treats anything non-finite
 * as "no data" — a NaN arriving from the api must not read as a real value.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return `0%`;
  return `${Math.round(Math.max(value, 0))}%`;
}

/**
 * `2530` → `"2\u00A0530"` — NBSP thousand groups (the RU convention, and the only
 * separator that never wraps mid-number). Negative and non-finite inputs fall
 * back to `0` — a plan or a count is never negative.
 */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '0';
  const rounded = Math.max(Math.round(value), 0);
  const digits = String(rounded);
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += NBSP;
    out += digits[i];
  }
  return out;
}

/**
 * ISO-8601 → `DD.MM.YYYY HH:mm`, rendered in UTC so the value reads the same in
 * every browser and every CI container (the api sends UTC). `null`, an empty
 * string and anything unparsable all render the absence marker — a bad timestamp
 * must not look like `Invalid Date` or `NaN.NaN.NaN`.
 *
 * @example formatDateTime('2026-09-03T06:00:00.000Z') // '03.09.2026 06:00'
 */
export function formatDateTime(iso: string | null | undefined, absent: string = MISSING): string {
  if (typeof iso !== 'string' || iso.trim() === '') return absent;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return absent;
  return formatUtcParts(date, absent);
}

/**
 * ISO-8601 → `DD.MM.YYYY HH:mm МСК` — the campaign's own clock. The api sends UTC and
 * the managers read MSK, so a timestamp shown in UTC was half a day behind what the
 * letter's header said (owner, T.V8: «11.09.2026 11:41 МСК», not the UTC minute).
 * Moscow has no DST, so the offset is the fixed +03:00 — no locale, no `Intl`, the same
 * determinism rule {@link formatDateTime} documents.
 *
 * @example formatDateTimeMsk('2026-09-11T08:41:00.000Z') // '11.09.2026 11:41 МСК'
 */
export function formatDateTimeMsk(iso: string | null | undefined, absent: string = MISSING): string {
  if (typeof iso !== 'string' || iso.trim() === '') return absent;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return absent;
  // +3h on the instant, then read the UTC parts: the same trick a fixed-offset
  // formatter plays, without trusting the host zone.
  return `${formatUtcParts(new Date(date.getTime() + MSK_OFFSET_MS), absent)} МСК`;
}

/**
 * ISO-8601 → `DD.MM.YYYY` — the date WITHOUT the time, for the values that are a
 * calendar day by definition rather than an instant (T.X1: «В системе с» holds the
 * export day of a snapshot, so `03.09.2026 03:00` would read as a processing time
 * again). Rendered in UTC like every other helper here: the api sends UTC midnight,
 * and MSK is +3, so both readers see the same calendar day.
 *
 * @example formatDate('2026-09-03T00:00:00.000Z') // '03.09.2026'
 */
export function formatDate(iso: string | null | undefined, absent: string = MISSING): string {
  const full = formatDateTime(iso, absent);
  // `formatDateTime` already returned the absence marker (or an exotic year) — pass it through.
  return full === absent ? absent : full.slice(0, 10);
}

/** Moscow is UTC+3 all year (no DST since 2014). */
const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

function formatUtcParts(date: Date, absent: string): string {
  const day = pad(date.getUTCDate());
  const month = pad(date.getUTCMonth() + 1);
  const year = String(date.getUTCFullYear());
  // Years outside 4 digits (or negative) are not dates this UI will ever meet.
  if (year.length !== 4) return absent;
  return `${day}.${month}.${year} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
